import React, { useState, useCallback } from "react";
import { TextField, InputAdornment, styled, Theme } from "@mui/material";
import { Typography } from "@platform/design-system";

import { formatCounterText } from "./helper";
import { CharacterLimitMode, type TextareaProps } from "./types";

const CharacterCount = styled(InputAdornment)(
  ({ theme }: { theme: Theme }) => ({
    "& .MuiTypography-root": {
      position: "absolute",
      right: 0,
      bottom: 0,
      padding: theme.spacing(1),
    },
  }),
);

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "hasCounter",
})<{ hasCounter?: boolean }>(({
  theme,
  hasCounter,
}: {
  theme: Theme;
  hasCounter?: boolean;
}) => {
  const spacingValue = theme.spacing(4);
  return {
    "& .MuiInputBase-root": {
      paddingBottom: hasCounter ? spacingValue : undefined,
    },
    "& .MuiInputBase-inputMultiline": {
      resize: "vertical",
      minHeight: "80px",
      overflowY: "auto",
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused fieldset": {
        borderWidth: "2px",
      },
    },
  };
});

const Textarea: React.FC<TextareaProps> = ({
  maxLength,
  minLength,
  characterLimitMode = CharacterLimitMode.Truncate,
  characterCounterVisible = true,
  counterText,
  defaultValue,
  value,
  onChange,
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const isControlled = value !== undefined;
  const currentValue = (isControlled ? value : internalValue) as string;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (
        characterLimitMode === CharacterLimitMode.Truncate &&
        maxLength !== undefined &&
        newValue.length > maxLength
      ) {
        return;
      }

      if (!isControlled) {
        setInternalValue(newValue);
      }
      onChange?.(event);
    },
    [characterLimitMode, maxLength, isControlled, onChange],
  );

  const hasCounter = Boolean(
    characterCounterVisible && (maxLength || minLength),
  );

  return (
    <StyledTextField
      data-testid="ds-textarea"
      {...props}
      multiline
      value={currentValue}
      onChange={handleChange}
      hasCounter={hasCounter}
      InputProps={{
        ...props.InputProps,
        endAdornment: hasCounter ? (
          <CharacterCounter
            value={currentValue.trim()}
            maxLength={maxLength}
            minLength={minLength}
            characterCounterVisible={characterCounterVisible}
            counterText={counterText}
          />
        ) : undefined,
        inputProps: {
          ...(characterLimitMode === CharacterLimitMode.Truncate &&
          maxLength !== undefined
            ? { maxLength }
            : {}),
          ...props.inputProps,
        },
      }}
    />
  );
};

const CharacterCounter = ({
  value,
  maxLength,
  minLength,
  characterCounterVisible,
  counterText,
}: {
  value: string;
  maxLength?: number;
  minLength?: number;
  characterCounterVisible?: boolean;
  counterText?: string;
}): React.ReactNode => {
  if (!characterCounterVisible || (!maxLength && !minLength)) return undefined;

  const isTooLong = maxLength !== undefined && value.length > maxLength;
  const isTooShort = minLength !== undefined && value.length < minLength;

  const displayText = counterText
    ? formatCounterText(counterText, value.length, maxLength, minLength)
    : `${value.length}${maxLength ? `/${maxLength}` : ""}`;

  return (
    <CharacterCount position="end">
      <Typography
        variant="caption"
        color={isTooLong || isTooShort ? "danger" : "textSecondary"}
        aria-live="polite"
      >
        {displayText}
      </Typography>
    </CharacterCount>
  );
};

export default Textarea;
